/**
 * Helfer fuer das Anpassen des Dashboards — ohne React, mit Tests.
 *
 * Zwei Dinge stehen hier: die Geometrie beim Ziehen am PC (an welcher Stelle
 * einer Zone landet ein Baustein, wo sitzt die Einfuegelinie) und die
 * Reihenfolge am Handy, das keine Spalten kennt und nur „nach oben / nach
 * unten" anbietet.
 */

import { DashboardLayout, WidgetId, Zone, ZONES, isWidgetId, moveWidget, nudgeWidget, zoneOf } from './dashboardLayout';

/** Eigener MIME-Typ, damit ein fremdes Drag (Datei, Text) keine Zone anspricht. */
export const DRAG_MIME = 'application/x-osia-widget';

/** Bausteine, die es nur am PC gibt — am Handy wird die Zeile so beschriftet. */
export const DESKTOP_ONLY: ReadonlySet<WidgetId> = new Set<WidgetId>(['projects', 'sendcenter']);

/** Kurzform der Zonen fuer Rahmen, Menue und Handy-Zeile. */
export const ZONE_SHORT: Record<Zone, string> = { top: 'Oben', main: 'Hauptspalte', side: 'Seitenspalte', bottom: 'Unten' };

// ─── Ziehen am PC ─────────────────────────────────────────────────────────

/**
 * Beim Start des Ziehens mitgeben. Gelesen wird erst beim Ablegen — Chrome
 * sperrt die Daten waehrend `dragover`, deshalb haelt die Ansicht den
 * gezogenen Baustein zusaetzlich im Zustand. text/plain, weil Firefox ohne
 * irgendeinen Wert gar nicht erst zieht.
 */
export const setDragPayload = (dt: DataTransfer, id: WidgetId): void => {
    dt.setData(DRAG_MIME, id);
    dt.setData('text/plain', id);
    dt.effectAllowed = 'move';
};

export const readDragPayload = (dt: DataTransfer): WidgetId | null => {
    const v = dt.getData(DRAG_MIME) || dt.getData('text/plain');
    return isWidgetId(v) ? v : null;
};

/**
 * An welcher Stelle der Zone landet der Zeiger? `mids` sind die senkrechten
 * Mitten der Nachbarn (ohne den gezogenen Baustein), in Zonenreihenfolge.
 * Ergebnis: wie viele Nachbarn oberhalb des Zeigers liegen — genau der
 * Index, den moveWidget erwartet.
 */
export const insertionIndex = (mids: ReadonlyArray<number>, y: number): number => {
    let n = 0;
    for (const m of mids) if (y > m) n++;
    return n;
};

export interface Band { top: number; bottom: number; }

/**
 * Senkrechte Lage der Einfuegelinie (relativ zum Zonenrahmen `frameTop`):
 * vor dem ersten Nachbarn, sonst mittig im Zwischenraum hinter dem Vorgaenger.
 * Ohne Nachbarn null — dann wird die ganze Zone hervorgehoben.
 */
export const insertionLineY = (bands: ReadonlyArray<Band>, index: number, frameTop: number, gap: number): number | null => {
    if (bands.length === 0) return null;
    const i = Math.max(0, Math.min(index, bands.length));
    const y = i === 0 ? bands[0].top - gap / 2 : bands[i - 1].bottom + gap / 2;
    return Math.round(y - frameTop);
};

/** Gleiche Bausteine an gleichen Plaetzen? Dann war ein Ablegen ein Nullvorgang. */
export const zonesEqual = (a: Record<Zone, ReadonlyArray<WidgetId>>, b: Record<Zone, ReadonlyArray<WidgetId>>): boolean =>
    ZONES.every(z => a[z].length === b[z].length && a[z].every((id, i) => id === b[z][i]));

/**
 * Ablegen per Ziehen: wie moveWidget, aber ein Ablegen an der alten Stelle
 * laesst das Layout unberuehrt — sonst wuerde die Vorlage grundlos zu
 * „eigene Anordnung" und der Stand grundlos gespeichert.
 */
export const dropWidget = (layout: DashboardLayout, id: WidgetId, zone: Zone, index: number, now = new Date()): DashboardLayout => {
    const next = moveWidget(layout, id, zone, index, now);
    return zonesEqual(layout.zones, next.zones) ? layout : next;
};

// ─── Reihenfolge am Handy ─────────────────────────────────────────────────

/**
 * Am Handy liegt zwischen der Zone `top` und dem Rest ein fester Abschnitt
 * (Standort, Suche). Ein Schritt ueber diese Grenze ist deshalb sichtbar und
 * wechselt nur die Zone; die Grenzen zwischen Haupt-, Seitenspalte und
 * `bottom` sieht man am Handy nicht — dort wird mit dem Nachbarn getauscht,
 * damit jeder Druck genau einen sichtbaren Platz bewegt.
 */
export const mobileNudge = (layout: DashboardLayout, id: WidgetId, delta: -1 | 1, now = new Date()): DashboardLayout => {
    const zone = zoneOf(layout, id);
    if (!zone) return layout;
    const list = layout.zones[zone];
    const j = list.indexOf(id) + delta;
    if (j >= 0 && j < list.length) return nudgeWidget(layout, id, delta, now);

    const zi = ZONES.indexOf(zone);
    if (delta > 0) {
        if (zone === 'top') return moveWidget(layout, id, 'main', 0, now);
        const next = ZONES.slice(zi + 1).find(z => layout.zones[z].length > 0);
        return next ? moveWidget(layout, id, next, 1, now) : layout;
    }
    if (zone === 'top') return layout;
    const prev = ZONES.slice(0, zi).reverse().find(z => layout.zones[z].length > 0);
    if (!prev || prev === 'top') return moveWidget(layout, id, 'top', layout.zones.top.length, now);
    return moveWidget(layout, id, prev, layout.zones[prev].length - 1, now);
};

/** Laesst sich der Baustein am Handy in diese Richtung bewegen? */
export const canMobileNudge = (layout: DashboardLayout, id: WidgetId, delta: -1 | 1): boolean =>
    mobileNudge(layout, id, delta) !== layout;

/** Die am Handy gelesene Reihenfolge, in zwei Abschnitte geteilt (siehe mobileNudge). */
export const mobileSections = (layout: DashboardLayout): { top: WidgetId[]; rest: WidgetId[] } => ({
    top: [...layout.zones.top],
    rest: [...layout.zones.main, ...layout.zones.side, ...layout.zones.bottom],
});
